import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, CircularProgress, Container, IconButton, Tooltip, Typography,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper,
  TextField, Pagination, MenuItem
} from "@mui/material";
import ArrowBack from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import moment from "moment";

import { addContracts, setYearToRequest } from "../../redux/slices/contractSlice";
import { getContracts } from "../../services/backend/ContractService";
import { getContractsLegal } from "../../services/backend/ContractLegalService";
import { PERMISSIONS } from "../../constants";
import ContractItem from "./ContractItem";
import usePaymentCutoff from "./usePaymentCutoff";
import PaymentCutoffNotice from "./PaymentCutoffNotice";

const PAGESIZE = 10;

const ContractList = ({ userType }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  // Aviso de fecha de corte (payment-monthly-cutoff) en las vistas del
  // contratista y del supervisor, con las naturalezas de sus contratos.
  const paymentCutoff = usePaymentCutoff();

  const section = userType ?? 'all';

  const allContracts = useSelector((state) => state.contracts.contracts[section]);
  const requestedYears = useSelector((state) => state.contracts.requestedYears[section]);
  // Contratos de personas jurídicas: vista del gestor (sección legal), del
  // supervisor (legalSupervisor) y de la empresa contratista (legalContractor).
  // Apoyo y financiera llegan con C7/C9.
  const isEntityUser = user?.userType === 'ENTITY';
  const legalSection = userType === 'supervisor'
    ? 'legalSupervisor'
    : userType === 'contractor' ? 'legalContractor' : 'legal';
  // Qué clase de contrato puede ver este usuario. Son dominios de permiso
  // distintos a propósito: contract.* son los naturales y contract_legal.* los
  // jurídicos, así que se puede gestionar una clase sin ver la otra. Quien lleve
  // los dos permisos sigue viendo la lista unificada de siempre.
  const canSeeNatural = !!user?.permissions?.includes(PERMISSIONS.CONTRACT.LIST);
  const canSeeLegal = !!user?.permissions?.includes(PERMISSIONS.CONTRACT_LEGAL.LIST);
  const canCreateNatural = !!user?.permissions?.includes(PERMISSIONS.CONTRACT.CREATE);
  const canCreateLegal = !!user?.permissions?.includes(PERMISSIONS.CONTRACT_LEGAL.CREATE);
  // El gestor (sin userType) carga jurídicos solo si lo tiene concedido; el
  // supervisor y la empresa entran por su propia asignación, no por el permiso
  // de listado, y el backend los acota a lo suyo.
  const loadsLegal = (!userType && canSeeLegal) || userType === 'supervisor'
    || (userType === 'contractor' && isEntityUser);
  // En la vista del gestor, los naturales también dependen de su permiso: sin él
  // la lista muestra únicamente jurídicos.
  const loadsNatural = !userType ? canSeeNatural : true;
  // Con una sola clase a la vista sobran el filtro y la columna de tipo.
  const showsBothPersonTypes = loadsNatural && loadsLegal;
  const legalContracts = useSelector((state) => state.contracts.contracts[legalSection]);
  const legalRequestedYears = useSelector((state) => state.contracts.requestedYears[legalSection]);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPersonType, setSelectedPersonType] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Naturales + jurídicos en las vistas del gestor y del supervisor; solo
  // naturales en las demás. Cada clase entra solo si el usuario la tiene
  // concedida, así que con un único permiso la lista queda con una sola clase.
  const sourceContracts = useMemo(() => {
    if (!loadsLegal) return allContracts;
    if (!loadsNatural) return legalContracts;
    if (allContracts === null && legalContracts === null) return null;
    return [...(allContracts || []), ...(legalContracts || [])];
  }, [allContracts, legalContracts, loadsLegal, loadsNatural]);

  // Transform dates to moment objects
  const contracts = useMemo(() => {
    if (sourceContracts === null) return null;
    if (!sourceContracts || sourceContracts.length === 0) return [];

    return sourceContracts.map(contract => ({
      ...contract,
      approvedAt: contract.approvedAt ? moment(contract.approvedAt) : null,
      finishedAt: contract.finishedAt ? moment(contract.finishedAt) : null,
      startedAt: contract.startedAt ? moment(contract.startedAt) : null,
      createdAt: contract.createdAt ? moment(contract.createdAt) : null,
      updatedAt: contract.updatedAt ? moment(contract.updatedAt) : null,
      news: contract.news?.map(n => ({
        ...n,
        startedAt: n.startedAt ? moment(n.startedAt) : null,
        finishedAt: n.finishedAt ? moment(n.finishedAt) : null,
      }))
    }));
  }, [sourceContracts]);

  // Contratos que cuentan para el aviso de corte: los vigentes este mes. Uno
  // cerrado o que terminó antes del mes no presenta ni aprueba nada, y
  // nombrarlo en el aviso solo confundiría. Va con su código para que el aviso
  // pueda decir a cuáles se refiere cuando los cortes son distintos.
  const cutoffItems = useMemo(() => {
    const monthStart = moment().startOf('month');
    return (contracts || [])
      .filter((c) => c.status !== 'CLOSED')
      .filter((c) => !c.finishedAt || !c.finishedAt.isValid() || !c.finishedAt.isBefore(monthStart))
      .map((c) => ({ nature: c.nature || null, code: c.contractCode }));
  }, [contracts]);

  const { filteredContract, maxPages } = useMemo(() => {
    let tmpContracts = [...(contracts || [])];

    // Filter by selected year
    if (selectedYear) {
      tmpContracts = tmpContracts.filter((cont) => {
        const [, , year] = cont.contractCode.split('_');
        return parseInt(year) === selectedYear;
      });
    }

    if (selectedStatus) {
      tmpContracts = tmpContracts.filter(c => c.status === selectedStatus);
    }

    // Tipo de persona: los naturales legados no tienen personType (sin
    // backfill), así que NATURAL se deriva de la ausencia del campo.
    if (selectedPersonType) {
      tmpContracts = tmpContracts.filter(c => (selectedPersonType === 'JURIDICA'
        ? c.personType === 'JURIDICA'
        : c.personType !== 'JURIDICA'));
    }

    if (searchText) {
      tmpContracts = tmpContracts.filter((cont) =>
        cont.contractCode.toLowerCase().includes(searchText.toLocaleLowerCase()) ||
        cont.supervisors?.map(s => `${s.name} ${s.lastName}`.toLowerCase()).join(' ').includes(searchText.toLowerCase()) ||
        `${cont.contractor?.name} ${cont.contractor?.lastName} ${cont.contractorNuip}`.toLowerCase().includes(searchText.toLowerCase()) ||
        // Contratos jurídicos: búsqueda por razón social o NIT de la empresa.
        `${cont.entity?.legalName} ${cont.entity?.nit}`.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Sort by year (desc), type (asc), and number (asc)
    tmpContracts = tmpContracts?.sort((a, b) => {
      const [typeA, numA, yearA] = a.contractCode.split('_');
      const [typeB, numB, yearB] = b.contractCode.split('_');

      if (yearA !== yearB) return yearB.localeCompare(yearA);
      if (typeA !== typeB) return typeA.localeCompare(typeB);
      return parseInt(numA) - parseInt(numB);
    });

    const maxPages = Math.ceil((tmpContracts?.length || 0) / PAGESIZE);
    tmpContracts = tmpContracts?.filter((__, idx) => idx >= page * PAGESIZE && idx < (page + 1) * PAGESIZE);

    return { filteredContract: tmpContracts, maxPages };
  }, [contracts, searchText, page, selectedStatus, selectedYear, selectedPersonType]);

  const getContractsAsync = async (year) => {
    setIsLoading(true);
    try {
      // El usuario-empresa no tiene contratos de persona natural: pedirlos
      // sería una consulta garantizada a vacío. Lo mismo vale para el gestor que
      // solo lleva el permiso jurídico, que además recibiría un 403.
      if (!isEntityUser && loadsNatural && !requestedYears?.includes(year)) {
        const response = await getContracts(user, userType, year);
        dispatch(addContracts({ contracts: response, section }));
        dispatch(setYearToRequest({ year, section }));
      }
      // Las vistas del gestor, del supervisor y de la empresa cargan jurídicos.
      if (loadsLegal && !legalRequestedYears?.includes(year)) {
        const legalResponse = await getContractsLegal(user, year, false, userType);
        if (legalResponse) {
          dispatch(addContracts({ contracts: legalResponse, section: legalSection }));
          dispatch(setYearToRequest({ year, section: legalSection }));
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // En la vista del gestor basta con poder ver UNA de las dos clases: quien
    // solo tenga contract_legal.list entra y ve únicamente jurídicos.
    if ((!userType && !canSeeNatural && !canSeeLegal)
      || (userType === 'supervisor' && !user.permissions.includes(PERMISSIONS.SUPERVISOR_CONTRACT.LIST))
      || (userType === 'support' && !user.permissions.includes(PERMISSIONS.SUPPORT_SUPERVISION.LIST))
      || (userType === 'contractor' && !user.permissions.includes(PERMISSIONS.CONTRACTOR.LIST))) {
        // A donde SÍ puede entrar, y nunca a '/': esa ruta es "que el Home
        // decida", así que cualquier regla del Home que apunte de vuelta aquí
        // convierte este guardia en un ciclo infinito. Ya pasó una vez.
        navigate(isEntityUser ? '/my-entity' : '/home', { replace: true });
        return;
      }
    getContractsAsync(selectedYear);
  }, [selectedYear]); // eslint-disable-line

  useEffect(() => {
    setPage(0);
  }, [selectedYear, searchText, selectedStatus, selectedPersonType]);

  return (
    <Container maxWidth="lg" style={{ padding: 3, marginTop: 10 }}>
      <Box sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 5,
      }}>
        <Typography variant="h4">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            style={{ margin: 16 }}
            onClick={() => navigate('/')}
          >
            Volver
          </Button>
          Contratos
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {user.roles?.includes('ADMIN') &&
            <Tooltip title="Configuración de contratos">
              <IconButton onClick={() => navigate('/contracts/config')}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          }
          {(canCreateNatural || canCreateLegal)
            && <Button
              variant="contained"
              // Quien solo puede crear jurídicos entra directo a su formulario;
              // el toggle de tipo de persona solo aparece si puede crear ambos.
              onClick={() => navigate(canCreateNatural ? '/contracts/new' : '/contracts/legal/new')}
            >
              Crear Nuevo Contrato
            </Button>
          }
        </Box>
      </Box>

      {(userType === 'contractor' || userType === 'supervisor') && (
        <PaymentCutoffNotice
          cutoff={paymentCutoff}
          natures={cutoffItems}
          role={userType}
          sx={{ mx: 2 }}
        />
      )}

      <Box sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        gap: 2,
        mb: 3
      }}>
        <Box sx={{ flex: 1, display: 'flex', gap: 1 }}>
          <TextField
            label="Buscar contrato"
            onChange={(evt) => {
              setSearchText(evt.target.value);
            }}
          />
          <TextField
            select
            label="Año"
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value={2024}>2024</MenuItem>
            <MenuItem value={2025}>2025</MenuItem>
            <MenuItem value={2026}>2026</MenuItem>
          </TextField>
          <TextField
            select
            label="Estado"
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="APPROVED">APPROVED</MenuItem>
            <MenuItem value="CLOSED">CLOSED</MenuItem>
            <MenuItem value="DRAFT">DRAFT</MenuItem>
            <MenuItem value="SUPERVISOR">SUPERVISOR</MenuItem>
          </TextField>
          {/* Solo tiene sentido con las dos clases a la vista: con una sola, el
              filtro únicamente ofrecería una opción que vacía la lista. */}
          {showsBothPersonTypes
            && <TextField
              select
              label="Tipo de Persona"
              value={selectedPersonType}
              onChange={e => setSelectedPersonType(e.target.value)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="NATURAL">Persona Natural</MenuItem>
              <MenuItem value="JURIDICA">Persona Jurídica</MenuItem>
            </TextField>
          }
        </Box>
        <Button
          variant="text"
          onClick={() => navigate('/contracts/closed')}
        >
          Ver contratos cerrados
        </Button>
      </Box>


      {contracts === null || isLoading ? (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <CircularProgress size={36} />
        </Box>
      ) : contracts.length === 0 ? (
        <Typography>No hay contratos para el año {selectedYear}</Typography>
      ) : filteredContract.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="Actions">
              <TableHead>
                <TableRow>
                  <TableCell>Código del contracto</TableCell>
                  {showsBothPersonTypes && <TableCell>Tipo de Persona</TableCell>}
                  <TableCell>Supervisor</TableCell>
                  <TableCell>Contratista</TableCell>
                  <TableCell>Objeto del contrato</TableCell>
                  <TableCell>Duración</TableCell>
                  <TableCell>Finaliza</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContract.map((contract) => (
                  <ContractItem
                    key={contract.contractCode}
                    contract={contract}
                    showPersonType={showsBothPersonTypes}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {maxPages > 1
            && <Pagination
              sx={{ justifyContent: 'center', mt: 1 }}
              count={maxPages}
              // page={page}
              onChange={(__, value) => setPage(value - 1)}
            />
          }
        </>
      ) : (
        <Typography>No hay contratos para el filtro seleccionado</Typography>
      )}
    </Container>
  )
};

export default ContractList;
